/**
 * Inbox layout route — renders the two-pane shell for all `/approvals/*`
 * routes. The list panel lives here; the reading pane is supplied by child
 * routes via `<Outlet>`:
 *
 *   /approvals              → approvals/index.tsx  (empty pane / auto-select first)
 *   /approvals/$approvalId  → approvals/$approvalId.tsx (reading pane)
 *
 * The `?status=` search param is validated here so the child routes inherit
 * it and deep-links to filtered views keep working. The status tabs render
 * in the Topbar (published via `WorkspaceShell.sectionTabs`) so the Inbox
 * chrome matches every other module's section tabs.
 */
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { WorkspaceShell, type ChromeSectionTab } from "../../features/workspace/index.ts";
import { InboxLayout } from "../../features/inbox/inbox-layout.tsx";
import { STATUS_FILTERS, type InboxStatus } from "../../features/inbox/index.ts";
import { trpc } from "../../lib/trpc.ts";

interface InboxSearch {
  status?: InboxStatus;
}

function isStatus(v: unknown): v is InboxStatus {
  return v === "pending" || v === "approved" || v === "rejected";
}

const STATUS_ICONS: Record<InboxStatus, typeof Clock> = {
  pending: Clock,
  approved: CheckCircle2,
  rejected: XCircle,
};

export const Route = createFileRoute("/(workspace)/approvals")({
  component: InboxLayoutRoute,
  validateSearch: (s: Record<string, unknown>): InboxSearch => {
    const raw = isStatus(s.status) ? s.status : isStatus(s.tab) ? s.tab : undefined;
    return raw && raw !== "pending" ? { status: raw } : {};
  },
});

function InboxLayoutRoute() {
  const search = Route.useSearch();
  const status: InboxStatus = search.status ?? "pending";

  // Same query key as InboxLayout's list query — React Query dedupes, so
  // this only reads the cached pendingCount for the topbar tab pill.
  const list = trpc.approvals.list.useQuery({ limit: 50, status });
  const pendingCount = list.data?.pendingCount ?? 0;

  const sectionTabs: ChromeSectionTab[] = STATUS_FILTERS.map((f) => ({
    label: f.label,
    icon: STATUS_ICONS[f.id],
    to: "/approvals",
    active: status === f.id,
    // Pending is the default view (no search param) and carries the count pill.
    ...(f.id === "pending" ? { count: pendingCount } : { search: { status: f.id } }),
  }));

  return (
    <WorkspaceShell
      breadcrumbs={[{ label: "Inbox" }]}
      disableViewportScroll
      contentClassName="max-w-none"
      sectionTabs={sectionTabs}
    >
      <InboxLayout status={status}>
        <Outlet />
      </InboxLayout>
    </WorkspaceShell>
  );
}
